import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';

function generateReferralCode(firstName: string, email: string): string {
  const base = (firstName.slice(0, 3) + email.slice(0, 4))
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, 'X');
  const suffix = Math.floor(1000 + Math.random() * 9000);
  return `${base}${suffix}`;
}

const SHARE_MILESTONES = [
  { count: 1, reward: 'Skip 50 spots', icon: 'ri-arrow-up-double-line' },
  { count: 3, reward: 'Skip 200 spots + Beta badge', icon: 'ri-medal-line' },
  { count: 5, reward: 'Founding Member status', icon: 'ri-vip-crown-line' },
  { count: 10, reward: 'Lifetime 30% discount', icon: 'ri-percent-line' },
];

function SuccessPanel({ firstName, email }: { firstName: string; email: string }) {
  const [copied, setCopied] = useState(false);
  const referralCode = useRef(generateReferralCode(firstName, email)).current;
  const referralUrl = `${window.location.origin}/early-access?ref=${referralCode}`;
  const [referralCount] = useState(0);

  const copyLink = () => {
    navigator.clipboard.writeText(referralUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const shareLinks = [
    {
      label: 'Twitter / X',
      icon: 'ri-twitter-x-line',
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        `I just claimed early access to WingmanFix — the AI copilot for pilots! 🛩️ Join me: ${referralUrl}`
      )}`,
    },
    {
      label: 'WhatsApp',
      icon: 'ri-whatsapp-line',
      href: `https://wa.me/?text=${encodeURIComponent(
        `Join me on WingmanFix — AI copilot for pilots! Use my link: ${referralUrl}`
      )}`,
    },
    {
      label: 'Facebook',
      icon: 'ri-facebook-line',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralUrl)}`,
    },
    {
      label: 'Email',
      icon: 'ri-mail-send-line',
      href: `mailto:?subject=${encodeURIComponent('Join me on WingmanFix!')}&body=${encodeURIComponent(
        `Hey!\n\nI just claimed early access to WingmanFix — an AI copilot app for pilots.\n\nUse my link to join: ${referralUrl}\n\nBlue skies!`
      )}`,
    },
  ];

  const nextMilestone = SHARE_MILESTONES.find((m) => m.count > referralCount) ?? SHARE_MILESTONES[SHARE_MILESTONES.length - 1];
  const progressPct = Math.min((referralCount / nextMilestone.count) * 100, 100);

  return (
    <div className="space-y-6">
      {/* Confirmation */}
      <div className="bg-[#00FFD1]/5 border border-[#00FFD1]/30 rounded-2xl p-10 text-center">
        <div className="w-20 h-20 flex items-center justify-center mx-auto mb-5 bg-[#00FFD1]/10 rounded-full border border-[#00FFD1]/30">
          <i className="ri-check-double-line text-[#00FFD1] text-4xl" />
        </div>
        <h2 className="text-3xl font-extrabold mb-2">You're cleared for departure!</h2>
        <p className="text-gray-400 leading-relaxed max-w-sm mx-auto mb-5">
          Welcome aboard, <strong className="text-white">{firstName}</strong>. Your early access spot is reserved. Invite fellow pilots to move up the queue.
        </p>
        <div className="inline-block bg-[#00FFD1]/10 border border-[#00FFD1]/20 rounded-full px-5 py-2 text-[#00FFD1] text-xs font-bold uppercase tracking-widest">
          Early Access Confirmed ✓
        </div>
      </div>

      {/* Referral link */}
      <div className="bg-white/5 border border-[#00FFD1]/20 rounded-2xl p-7">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-links-line text-[#00FFD1]" />
          </div>
          <h3 className="text-xs font-bold text-[#00FFD1] uppercase tracking-widest">Your Referral Link</h3>
        </div>
        <p className="text-xs text-gray-500 mb-4">Every pilot who joins through your link moves you up the list.</p>
        <div className="flex items-center gap-2 bg-[#0A1628] border border-white/20 rounded-xl px-4 py-3 mb-5">
          <span className="flex-1 text-xs text-gray-300 truncate font-mono">{referralUrl}</span>
          <button
            onClick={copyLink}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
              copied
                ? 'bg-[#00FFD1]/20 text-[#00FFD1] border border-[#00FFD1]/40'
                : 'bg-white/10 text-white hover:bg-white/20 border border-white/10'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'} />
            </div>
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 font-semibold">Share via</p>
        <div className="grid grid-cols-4 gap-3">
          {shareLinks.map((s) => (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 border border-white/10 rounded-xl py-3 px-2 hover:bg-white/10 transition-all cursor-pointer"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className={`${s.icon} text-white text-lg`} />
              </div>
              <span className="text-xs text-gray-400">{s.label}</span>
            </a>
          ))}
        </div>
      </div>

      {/* Progress */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-7">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-bar-chart-grouped-line text-[#00FFD1]" />
            </div>
            <h3 className="text-xs font-bold text-[#00FFD1] uppercase tracking-widest">Referral Rewards</h3>
          </div>
          <div className="bg-[#00FFD1]/10 border border-[#00FFD1]/20 rounded-full px-3 py-1 text-[#00FFD1] text-xs font-bold">
            {referralCount} / {nextMilestone.count}
          </div>
        </div>
        <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-5">
          <div
            className="h-full bg-gradient-to-r from-[#00FFD1] to-[#00A3E0] rounded-full transition-all duration-700"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <div className="space-y-3">
          {SHARE_MILESTONES.map((m) => {
            const unlocked = referralCount >= m.count;
            return (
              <div
                key={m.count}
                className={`flex items-center gap-4 rounded-xl px-4 py-3 border transition-all ${
                  unlocked ? 'border-[#00FFD1]/40 bg-[#00FFD1]/5' : 'border-white/10 opacity-60'
                }`}
              >
                <div className={`w-9 h-9 flex items-center justify-center rounded-full border ${unlocked ? 'border-[#00FFD1]/50 bg-[#00FFD1]/10' : 'border-white/20 bg-white/5'}`}>
                  <i className={`${m.icon} text-base ${unlocked ? 'text-[#00FFD1]' : 'text-gray-500'}`} />
                </div>
                <div className="flex-1">
                  <div className={`text-sm font-semibold ${unlocked ? 'text-white' : 'text-gray-500'}`}>{m.reward}</div>
                  <div className="text-xs text-gray-600">Invite {m.count} pilot{m.count > 1 ? 's' : ''}</div>
                </div>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={unlocked ? 'ri-check-circle-fill text-[#00FFD1]' : 'ri-lock-line text-gray-600'} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex gap-3">
        <Link
          to="/"
          className="flex-1 text-center border border-white/20 text-gray-300 hover:text-white hover:border-white/40 font-semibold px-4 py-3 rounded-lg transition-all cursor-pointer whitespace-nowrap text-sm"
        >
          Back to Home
        </Link>
        <Link
          to="/features"
          className="flex-1 text-center border border-[#00FFD1]/40 text-[#00FFD1] hover:bg-[#00FFD1]/10 font-semibold px-4 py-3 rounded-lg transition-all cursor-pointer whitespace-nowrap text-sm"
        >
          Explore Features
        </Link>
      </div>
    </div>
  );
}

export default function EarlyAccessForm() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(0);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    aircraftType: '',
    flyingHours: '',
    homeAirport: '',
    trainingGoals: '',
    heardFrom: '',
    newsletter: false,
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setForm((prev) => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      if (name === 'trainingGoals') setCharCount(value.length);
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) {
      setError('Training goals must be 500 characters or fewer.');
      return;
    }
    setError('');
    setLoading(true);
    const body = new URLSearchParams();
    Object.entries(form).forEach(([key, val]) => body.append(key, String(val)));
    try {
      await fetch('https://readdy.ai/api/form/d6sasdra90d3mf3ehbj0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#00FFD1]/60 transition-all';
  const selectClass =
    'w-full bg-[#0A1628] border border-white/20 rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-[#00FFD1]/60 transition-all cursor-pointer appearance-none';
  const labelClass = 'block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2';

  return (
    <section id="signup-form" className="py-24 px-6 bg-[#060E1E]">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-[1fr_520px] gap-12 items-start">

          {/* Left — value prop sidebar */}
          {!submitted && (
            <div className="space-y-8 lg:sticky lg:top-28">
              <div>
                <p className="text-xs font-bold text-[#00FFD1] uppercase tracking-widest mb-3">Claim Your Spot</p>
                <h2 className="text-4xl font-extrabold leading-tight mb-4">
                  Join <span className="text-[#00FFD1]">347+ pilots</span> already on the list
                </h2>
                <p className="text-gray-400 text-base leading-relaxed">
                  Fill in your pilot profile below. We use this to match you with the right features and prioritize your access based on your training needs.
                </p>
              </div>

              {/* What you get */}
              <div className="space-y-4">
                {[
                  { icon: 'ri-gift-line', title: 'Free during entire beta', desc: 'No credit card, no hidden fees.' },
                  { icon: 'ri-rocket-2-line', title: 'Priority access', desc: 'Earlier signup = earlier access.' },
                  { icon: 'ri-vip-crown-line', title: 'Founding Member perks', desc: 'Exclusive badge + lifetime discount.' },
                  { icon: 'ri-road-map-line', title: 'Shape the roadmap', desc: 'Your feedback drives what we build next.' },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#00FFD1]/10 border border-[#00FFD1]/20 flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-[#00FFD1] text-lg`} />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-white">{item.title}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Mini testimonial */}
              <div className="bg-white/4 border border-white/10 rounded-2xl p-6">
                <div className="flex gap-1 mb-3">
                  {[1,2,3,4,5].map((i) => (
                    <div key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-[#00FFD1] text-sm" />
                    </div>
                  ))}
                </div>
                <p className="text-gray-300 text-sm italic leading-relaxed mb-4">
                  &ldquo;Signed up in under 2 minutes. Got my beta invite 3 weeks later. Best decision I made for my training.&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full overflow-hidden border border-white/20 flex-shrink-0">
                    <img
                      src="https://public.readdy.ai/ai/img_res/c102200b14d40c1945ab842a92795021.jpg"
                      alt="Pilot"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-white">Daniel W.</div>
                    <div className="text-xs text-gray-500">PPL · 180 hrs</div>
                  </div>
                </div>
              </div>

              {/* Trust badges */}
              <div className="flex flex-wrap gap-3">
                {[
                  { icon: 'ri-lock-line', label: 'Encrypted data' },
                  { icon: 'ri-eye-off-line', label: 'Never sold' },
                  { icon: 'ri-delete-bin-line', label: 'Delete anytime' },
                ].map((b) => (
                  <div key={b.label} className="flex items-center gap-1.5 text-xs text-gray-500 bg-white/4 border border-white/10 rounded-full px-3 py-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${b.icon} text-[#00FFD1]/60 text-xs`} />
                    </div>
                    {b.label}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Right — Form or Success */}
          <div className={submitted ? 'lg:col-span-2 max-w-2xl mx-auto w-full' : ''}>
            {submitted ? (
              <SuccessPanel firstName={form.firstName} email={form.email} />
            ) : (
              <form
                data-readdy-form
                id="early-access-signup-form"
                onSubmit={handleSubmit}
                className="bg-white/4 border border-[#00FFD1]/20 rounded-2xl p-8 space-y-8"
              >
                {/* Personal Info */}
                <div>
                  <h3 className="text-xs font-bold text-[#00FFD1] uppercase tracking-widest mb-5 flex items-center gap-2">
                    <i className="ri-user-line" /> Personal Info
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className={labelClass}>First Name <span className="text-[#00FFD1]">*</span></label>
                      <input type="text" name="firstName" required value={form.firstName} onChange={handleChange} placeholder="Alex" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Last Name <span className="text-[#00FFD1]">*</span></label>
                      <input type="text" name="lastName" required value={form.lastName} onChange={handleChange} placeholder="Reynolds" className={inputClass} />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Email Address <span className="text-[#00FFD1]">*</span></label>
                    <input type="email" name="email" required value={form.email} onChange={handleChange} placeholder="captain@example.com" className={inputClass} />
                  </div>
                </div>

                <div className="border-t border-white/10" />

                {/* Flying Background */}
                <div>
                  <h3 className="text-xs font-bold text-[#00FFD1] uppercase tracking-widest mb-5 flex items-center gap-2">
                    <i className="ri-flight-takeoff-line" /> Flying Background
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className={labelClass}>Pilot Role <span className="text-[#00FFD1]">*</span></label>
                      <select name="role" required value={form.role} onChange={handleChange} className={selectClass}>
                        <option value="" disabled>Select your role…</option>
                        <option value="Student Pilot">Student Pilot</option>
                        <option value="Private Pilot (PPL)">Private Pilot (PPL)</option>
                        <option value="Instrument Rated (IFR)">Instrument Rated (IFR)</option>
                        <option value="Commercial Pilot (CPL)">Commercial Pilot (CPL)</option>
                        <option value="CFI / Flight Instructor">CFI / Flight Instructor</option>
                        <option value="ATP / Airline Pilot">ATP / Airline Pilot</option>
                        <option value="Aviation Enthusiast">Aviation Enthusiast</option>
                      </select>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className={labelClass}>Primary Aircraft <span className="text-[#00FFD1]">*</span></label>
                        <select name="aircraftType" required value={form.aircraftType} onChange={handleChange} className={selectClass}>
                          <option value="" disabled>Select aircraft…</option>
                          <option value="Single-Engine Piston (SEP)">Single-Engine Piston (SEP)</option>
                          <option value="Multi-Engine Piston (MEP)">Multi-Engine Piston (MEP)</option>
                          <option value="Turboprop">Turboprop</option>
                          <option value="Light Sport Aircraft (LSA)">Light Sport Aircraft (LSA)</option>
                          <option value="Helicopter">Helicopter</option>
                          <option value="Glider / Sailplane">Glider / Sailplane</option>
                          <option value="Jet / Business Aviation">Jet / Business Aviation</option>
                          <option value="Simulator Only">Simulator Only</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelClass}>Total Flying Hours <span className="text-[#00FFD1]">*</span></label>
                        <select name="flyingHours" required value={form.flyingHours} onChange={handleChange} className={selectClass}>
                          <option value="" disabled>Select range…</option>
                          <option value="0–50 hours">0–50 hours (Student)</option>
                          <option value="50–200 hours">50–200 hours</option>
                          <option value="200–500 hours">200–500 hours</option>
                          <option value="500–1,000 hours">500–1,000 hours</option>
                          <option value="1,000–5,000 hours">1,000–5,000 hours</option>
                          <option value="5,000+ hours">5,000+ hours</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>Home Airport (ICAO / IATA)</label>
                      <input type="text" name="homeAirport" value={form.homeAirport} onChange={handleChange} placeholder="e.g. KLAX, EGLL, YSSY" className={inputClass} />
                    </div>
                  </div>
                </div>

                <div className="border-t border-white/10" />

                {/* Goals */}
                <div>
                  <h3 className="text-xs font-bold text-[#00FFD1] uppercase tracking-widest mb-5 flex items-center gap-2">
                    <i className="ri-target-line" /> Your Goals
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className={labelClass}>What are your main training goals?</label>
                      <textarea
                        name="trainingGoals"
                        value={form.trainingGoals}
                        onChange={handleChange}
                        rows={4}
                        maxLength={500}
                        placeholder="e.g. Improve IFR proficiency, work on crosswind landings, prepare for instrument checkride…"
                        className={`${inputClass} resize-none`}
                      />
                      <div className={`text-right text-xs mt-1 ${charCount > 480 ? 'text-amber-400' : 'text-gray-600'}`}>
                        {charCount}/500
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>How did you hear about WingmanFix?</label>
                      <select name="heardFrom" value={form.heardFrom} onChange={handleChange} className={selectClass}>
                        <option value="" disabled>Select source…</option>
                        <option value="Social Media">Social Media</option>
                        <option value="Friend / Colleague">Friend / Colleague</option>
                        <option value="Flight School">Flight School</option>
                        <option value="Aviation Forum / Reddit">Aviation Forum / Reddit</option>
                        <option value="Podcast">Podcast</option>
                        <option value="Google Search">Google Search</option>
                        <option value="CFI Recommendation">CFI Recommendation</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="border-t border-white/10" />

                {/* Newsletter */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative mt-0.5 flex-shrink-0">
                    <input type="checkbox" name="newsletter" checked={form.newsletter} onChange={handleChange} className="sr-only" />
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${form.newsletter ? 'bg-[#00FFD1] border-[#00FFD1]' : 'border-white/30 bg-white/5 group-hover:border-[#00FFD1]/50'}`}>
                      {form.newsletter && <i className="ri-check-line text-[#060E1E] text-xs font-bold" />}
                    </div>
                  </div>
                  <span className="text-sm text-gray-400 leading-relaxed">
                    Send me product updates, aviation tips, and early access news. No spam — unsubscribe anytime.
                  </span>
                </label>

                {error && (
                  <p className="text-red-400 text-xs flex items-center gap-1.5">
                    <i className="ri-error-warning-line" /> {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading || charCount > 500}
                  className="w-full bg-[#00FFD1] hover:bg-[#00e6bc] disabled:opacity-60 text-[#060E1E] font-extrabold py-4 rounded-lg transition-all hover:scale-[1.02] shadow-lg shadow-[#00FFD1]/20 cursor-pointer whitespace-nowrap text-sm uppercase tracking-wider"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin" /> Submitting…
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-rocket-2-line" /> Claim My Early Access Spot
                    </span>
                  )}
                </button>

                <p className="text-center text-xs text-gray-600">
                  No credit card required. Your data stays private.{' '}
                  <Link to="/privacy" className="text-[#00FFD1]/60 hover:text-[#00FFD1] underline transition-colors cursor-pointer">
                    Privacy Policy
                  </Link>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
